import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";

interface Props {
  workspaceId: string;
}

interface ReportRow {
  id: string;
  user_id: string;
  week_label: string;
  week_start: string;
  done_this_week: string;
  difficulties: string;
  next_week_plan: string;
  status: "submitted" | "reviewed" | "returned";
  admin_comment: string | null;
  submitted_at: string;
  reviewed_at: string | null;
  // joined
  display_name?: string;
  email?: string;
}

const STATUS_CONFIG = {
  submitted: { label: "검토 대기", color: "#7A9BFF", icon: "ri-time-line", bg: "rgba(122,155,255,0.08)", border: "rgba(122,155,255,0.2)" },
  reviewed: { label: "완료", color: "#00C896", icon: "ri-checkbox-circle-fill", bg: "rgba(0,200,150,0.08)", border: "rgba(0,200,150,0.2)" },
  returned: { label: "반려", color: "#E8375F", icon: "ri-arrow-go-back-line", bg: "rgba(232,55,95,0.08)", border: "rgba(232,55,95,0.2)" },
};

function formatWeekRange(weekStart: string): string {
  const mon = new Date(weekStart);
  const fri = new Date(mon);
  fri.setDate(mon.getDate() + 4);
  const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${mon.getFullYear()}년 ${mon.getMonth() + 1}월 ${fmt(mon)}~${fmt(fri)}`;
}

const FIELDS: { key: keyof ReportRow; label: string; icon: string }[] = [
  { key: "done_this_week", label: "이번 주에 한 일", icon: "ri-checkbox-line" },
  { key: "difficulties", label: "어려웠던 점 & 배운 것", icon: "ri-lightbulb-line" },
  { key: "next_week_plan", label: "다음 주 계획", icon: "ri-calendar-check-line" },
];

export default function WeeklyReportManager({ workspaceId }: Props) {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<ReportRow | null>(null);
  const [comment, setComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"all" | "submitted" | "reviewed" | "returned">("all");
  const [filterWeek, setFilterWeek] = useState<string>("all");

  const fetchReports = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);

    const { data: reps } = await supabase
      .from("weekly_reports")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("submitted_at", { ascending: false });

    if (!reps || reps.length === 0) { setReports([]); setLoading(false); return; }

    const userIds = [...new Set(reps.map((r: ReportRow) => r.user_id))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, display_name, email")
      .in("id", userIds);

    const profMap: Record<string, { display_name: string; email: string }> = {};
    (profiles ?? []).forEach((p: { id: string; display_name: string; email: string }) => { profMap[p.id] = p; });

    const merged: ReportRow[] = reps.map((r: ReportRow) => ({
      ...r,
      display_name: profMap[r.user_id]?.display_name ?? "익명",
      email: profMap[r.user_id]?.email ?? "-",
    }));

    setReports(merged);
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleAction = async (action: "reviewed" | "returned") => {
    if (!selectedReport) return;
    setActionLoading(true);

    await supabase.from("weekly_reports").update({
      status: action,
      admin_comment: comment.trim() || null,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }).eq("id", selectedReport.id);

    setActionLoading(false);
    setComment("");
    setSelectedReport(null);
    await fetchReports();
  };

  // 주차 목록 추출
  const weekOptions = [...new Set(reports.map(r => r.week_label))].sort().reverse();

  const filtered = reports.filter(r => {
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (filterWeek !== "all" && r.week_label !== filterWeek) return false;
    return true;
  });

  const pendingCount = reports.filter(r => r.status === "submitted").length;
  const reviewedCount = reports.filter(r => r.status === "reviewed").length;
  const returnedCount = reports.filter(r => r.status === "returned").length;

  return (
    <div className="flex gap-5 h-full" style={{ minHeight: 600 }}>
      {/* 목록 패널 */}
      <div className="flex flex-col" style={{ width: 400 }}>
        {/* 통계 요약 */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: "검토 대기", count: pendingCount, color: "#7A9BFF", status: "submitted" as const },
            { label: "완료", count: reviewedCount, color: "#00C896", status: "reviewed" as const },
            { label: "반려", count: returnedCount, color: "#E8375F", status: "returned" as const },
          ].map(s => (
            <button
              key={s.label}
              type="button"
              onClick={() => setFilterStatus(prev => prev === s.status ? "all" : s.status)}
              className={`rounded-xl border px-3 py-2.5 text-center transition-all cursor-pointer ${filterStatus === s.status ? "ring-1" : ""}`}
              style={{
                borderColor: filterStatus === s.status ? `${s.color}50` : "rgba(255,255,255,0.05)",
                backgroundColor: filterStatus === s.status ? `${s.color}10` : "rgba(17,17,18,1)",
                ringColor: s.color,
              }}
            >
              <div className="text-xl font-bold tabular-nums" style={{ color: s.color }}>{s.count}</div>
              <div className="text-[#555555] text-[10px]">{s.label}</div>
            </button>
          ))}
        </div>

        {/* 주차 필터 */}
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-calendar-line text-[#444444] text-sm"></i>
          <select
            value={filterWeek}
            onChange={e => setFilterWeek(e.target.value)}
            className="flex-1 bg-[#111112] text-[#888888] text-xs px-2.5 py-1.5 rounded-lg border border-white/[0.06] focus:outline-none cursor-pointer"
          >
            <option value="all">전체 주차</option>
            {weekOptions.map(w => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
          <button type="button" onClick={fetchReports}
            className="w-7 h-7 flex items-center justify-center text-[#444444] hover:text-[#777777] cursor-pointer transition-colors">
            <i className="ri-refresh-line text-sm"></i>
          </button>
        </div>

        {/* 보고서 목록 */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-white/[0.02] rounded-xl animate-pulse border border-white/[0.04]" />
            ))
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <i className="ri-file-text-line text-3xl text-[#222222] block mb-3"></i>
              <p className="text-[#3D3D3D] text-xs">제출된 보고서가 없어요</p>
            </div>
          ) : (
            filtered.map(r => {
              const cfg = STATUS_CONFIG[r.status];
              const isSelected = selectedReport?.id === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => { setSelectedReport(r); setComment(r.admin_comment ?? ""); }}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all cursor-pointer ${
                    isSelected ? "border-[#FF9F43]/30 bg-[#FF9F43]/[0.04]" : "border-white/[0.05] bg-[#111112] hover:border-white/[0.1] hover:bg-white/[0.02]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                        style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}
                      >
                        {(r.display_name ?? "?")[0]?.toUpperCase()}
                      </div>
                      <span className="text-[#CCCCCC] text-xs font-medium">{r.display_name}</span>
                    </div>
                    <span
                      className="flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full border shrink-0"
                      style={{ color: cfg.color, borderColor: `${cfg.color}30`, backgroundColor: cfg.bg }}
                    >
                      <i className={`${cfg.icon} text-xs`}></i>
                      {cfg.label}
                    </span>
                  </div>
                  <div className="text-[#444444] text-[10px] mb-1">{formatWeekRange(r.week_start)}</div>
                  <p className="text-[#555555] text-[10px] leading-relaxed line-clamp-2">{r.done_this_week}</p>
                  <div className="text-[#333333] text-[9px] mt-1.5 tabular-nums">
                    {new Date(r.submitted_at).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" })}
                    {" "}
                    {new Date(r.submitted_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })} 제출
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* 상세 패널 */}
      <div className="flex-1 bg-[#111112] rounded-xl border border-white/[0.05] flex flex-col overflow-hidden">
        {!selectedReport ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/[0.03] border border-white/[0.05]">
              <i className="ri-file-text-line text-2xl text-[#333333]"></i>
            </div>
            <div className="text-center">
              <p className="text-[#555555] text-sm">보고서를 선택해 내용을 확인하세요</p>
              {pendingCount > 0 && (
                <p className="text-[#7A9BFF]/70 text-xs mt-1">검토 대기 {pendingCount}건이 있어요</p>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* 상세 헤더 */}
            <div className="px-5 py-4 border-b border-white/[0.05] flex items-center justify-between shrink-0">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: `${STATUS_CONFIG[selectedReport.status].color}15`, color: STATUS_CONFIG[selectedReport.status].color }}
                  >
                    {(selectedReport.display_name ?? "?")[0]?.toUpperCase()}
                  </div>
                  <span className="text-[#F2F2F2] text-sm font-semibold">{selectedReport.display_name}</span>
                  <span className="text-[#3D3D3D] text-[10px]">{selectedReport.email}</span>
                </div>
                <div className="text-[#444444] text-[10px]">{formatWeekRange(selectedReport.week_start)}</div>
              </div>
              <button type="button" onClick={() => setSelectedReport(null)}
                className="w-7 h-7 flex items-center justify-center text-[#3D3D3D] hover:text-[#777777] cursor-pointer transition-colors">
                <i className="ri-close-line text-base"></i>
              </button>
            </div>

            {/* 보고서 내용 */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {FIELDS.map(f => (
                <div key={f.key} className="bg-[#0C0C0E] rounded-lg border border-white/[0.05] px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <i className={`${f.icon} text-xs text-[#555555]`}></i>
                    <span className="text-[#555555] text-[10px] uppercase tracking-widest">{f.label}</span>
                  </div>
                  <p className="text-[#AAAAAA] text-xs leading-relaxed whitespace-pre-wrap">
                    {String(selectedReport[f.key] ?? "")}
                  </p>
                </div>
              ))}

              {/* 기존 코멘트 표시 */}
              {selectedReport.status !== "submitted" && selectedReport.admin_comment && (
                <div
                  className="rounded-lg border px-4 py-3"
                  style={{
                    borderColor: STATUS_CONFIG[selectedReport.status].border,
                    backgroundColor: STATUS_CONFIG[selectedReport.status].bg,
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <i className="ri-user-star-line text-xs" style={{ color: STATUS_CONFIG[selectedReport.status].color }}></i>
                    <span className="text-[10px] font-semibold" style={{ color: STATUS_CONFIG[selectedReport.status].color }}>
                      이전 코멘트
                    </span>
                  </div>
                  <p className="text-[#888888] text-xs leading-relaxed">{selectedReport.admin_comment}</p>
                </div>
              )}
            </div>

            {/* 피드백 & 액션 */}
            <div className="px-5 py-4 border-t border-white/[0.05] space-y-3 shrink-0">
              <div>
                <label className="text-[#555555] text-xs mb-1.5 block flex items-center gap-1.5">
                  <i className="ri-chat-3-line text-sm"></i>
                  강사 코멘트 <span className="text-[#3D3D3D]">(선택)</span>
                </label>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="학생에게 전달할 피드백을 작성하세요. 반려 시에는 구체적인 수정 방향을 알려주세요."
                  rows={3}
                  maxLength={500}
                  className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs rounded-lg px-3 py-2.5 border border-white/[0.08] focus:outline-none focus:border-[#FF9F43]/40 placeholder-[#2A2A2A] resize-none"
                />
                <div className="text-right text-[#2A2A2A] text-[9px] mt-0.5">{comment.length}/500</div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleAction("returned")}
                  disabled={actionLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#E8375F]/30 bg-[#E8375F]/[0.08] text-[#E8375F] text-sm font-semibold hover:bg-[#E8375F]/15 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <i className="ri-arrow-go-back-line text-sm"></i>반려 (수정 요청)
                </button>
                <button
                  type="button"
                  onClick={() => handleAction("reviewed")}
                  disabled={actionLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#00C896] text-white text-sm font-semibold hover:bg-[#00DDA8] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {actionLoading
                    ? <><i className="ri-loader-4-line animate-spin text-sm"></i>처리 중...</>
                    : <><i className="ri-checkbox-circle-line text-sm"></i>검토 완료</>
                  }
                </button>
              </div>
              <p className="text-[#2A2A2A] text-[9px] text-center">
                검토 완료 시 학생 보고서에 &quot;완료&quot; 표시 + 코멘트가 전달됩니다
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
